import React, { useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Button, Avatar, Dropdown, Badge, Grid, Typography, Space } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  BookOutlined,
  ShopOutlined,
  HomeOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuOutlined,
  TeamOutlined,
  BankOutlined,
  TagsOutlined,
  ControlOutlined,
  UnorderedListOutlined,
  CrownOutlined,
  EyeInvisibleOutlined,
  KeyOutlined,
  FolderOutlined,
  // 票务管理相关图标
  AccountBookOutlined,
  ScanOutlined,
  // 玩家回放相关图标
  PlayCircleOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useAuthStore } from './store/auth'
import { useNotificationStore } from './store/notifications'
import { WorldProvider } from './contexts/WorldContext'
import Dashboard from './pages/Dashboard'
import UserManagement from './pages/UserManagement'
import ScriptManagement from './pages/ScriptManagement'
import MerchantManagement from './pages/MerchantManagement'
import VenueManagement from './pages/VenueManagement'
import RoomManagement from './pages/RoomManagement'
import Settings from './pages/Settings'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import RoleManagement from './pages/RoleManagement'
import DeviceManagement from './pages/DeviceManagement'
import DeviceCatalogManagement from './pages/DeviceCatalogManagement'
import ConfigBlockManagement from './pages/ConfigBlockManagement'
import CharacterManagement from './pages/CharacterManagement'
import FileManagement from './pages/FileManagement'
import {
  ScriptGenerationTaskList,
  ScriptGenerationCreateTask,
  ScriptGenerationConfigPreview,
  ScriptGenerationTaskDetailRouter,
} from './pages/ScriptGeneration'
import { ScriptGenerationV5CreateTask } from './pages/ScriptGenerationV5'

// 票务管理子页面（保留配置页，并新增一个只读场次数据页）
import TicketingAnonymousConfig from './pages/ticketing/AnonymousConfig'
import TicketingSessionDataViewer from './pages/ticketing/SessionDataViewer'
import TicketingSessionPricing from './pages/ticketing/SessionPricing'

// 玩家回放中心 (Phase 2)
import { PlaybackList, PlaybackDetail } from './pages/playback'

// 系统管理子页面
import SystemConfigManagement from './pages/system/SystemConfigManagement'
import VIPLevelConfig from './pages/system/VIPLevelConfig'

// 标签管理页面
import TagDefinitionManagement from './pages/TagDefinitionManagement'

// NFC 管理页面
import NfcManagement from './pages/NfcManagement'

// API Key 管理页面
import ApiKeysManagement from './pages/ApiKeysManagement'

import './App.css'

const { Header, Sider, Content } = Layout
const { Title, Text } = Typography
const { useBreakpoint } = Grid

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const { isAuthenticated, user, logout } = useAuthStore()
  const { notifications, unreadCount, markAsRead } = useNotificationStore()
  const navigate = useNavigate()
  const location = useLocation()
  const screens = useBreakpoint()

  const isMobile = !screens.md

  // Helper function to check admin roles
  const isAdmin = () => {
    return ['super_admin', 'platform_admin', 'admin'].includes(user?.role || '')
  }

  // isSuperAdmin 已预留，用于未来需要超级管理员权限的功能

  // Menu items based on user permissions
  // 注意：v3 归档后，被新后台接管的菜单项已移除（详见 services/admin-portal-api/scripts/archived_prefixes.txt）
  // A 档保留的菜单项：仪表盘 / 商户 / 场馆 / 房间 / 设备 / 用户 / 角色 / 剧本 / 角色 / 配置块 / NFC / 标签 / 系统配置 / VIP 等级 / 匿名票 / 场次价格 / 场次数据只读页 / 回放中心 / 账号安全
  const getMenuItems = () => {
    const baseItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: '仪表盘',
      },
    ]

    // Admin-only menu items (user management, system config)
    if (isAdmin()) {
      // 系统管理子菜单（恢复 /files 用于 OSS 文件管理）
      const systemSubMenu: MenuProps['items'] = [
        { key: '/users', label: '用户管理', icon: <UserOutlined /> },
        { key: '/roles', label: '角色权限', icon: <TeamOutlined /> },
        { type: 'divider' as const },
        { key: '/system/configs', label: '系统配置', icon: <SettingOutlined /> },
        { key: '/system/vip-levels', label: 'VIP等级', icon: <CrownOutlined /> },
        { type: 'divider' as const },
        { key: '/files', label: '文件管理', icon: <FolderOutlined /> },
        { key: '/tags/definitions', label: '标签管理', icon: <TagsOutlined /> },
      ]
      // 商户管理子菜单（A 档全保留）
      const merchantSubMenu: MenuProps['items'] = [
        { key: '/merchants', label: '商家管理', icon: <ShopOutlined /> },
        { key: '/venues', label: '场馆管理', icon: <HomeOutlined /> },
        { key: '/rooms', label: '房间管理', icon: <BankOutlined /> },
        { key: '/devices', label: '设备管理', icon: <ControlOutlined /> },
        { key: '/device-catalog', label: '设备能力目录', icon: <ControlOutlined /> },
        { type: 'divider' as const },
        { key: '/api-keys', label: 'API Key 管理', icon: <KeyOutlined /> },
      ]
      // 内容管理子菜单（恢复 /script-generation，/ads 仍归档）
      const contentSubMenu: MenuProps['items'] = [
        { key: '/scripts', label: '剧本管理', icon: <BookOutlined /> },
        { key: '/characters', label: '角色管理', icon: <TeamOutlined /> },
        { key: '/config-blocks', label: '配置块管理', icon: <UnorderedListOutlined /> },
        { key: '/script-generation', label: 'AI 剧本生成', icon: <BookOutlined /> },
        { type: 'divider' as const },
        { key: '/nfc', label: 'NFC 管理', icon: <ScanOutlined /> },
      ]

      baseItems.push(
        {
          key: '/system',
          icon: <SettingOutlined />,
          label: '系统管理',
          children: systemSubMenu,
        } as any,
        {
          key: '/merchant',
          icon: <ShopOutlined />,
          label: '商户管理',
          children: merchantSubMenu,
        } as any,
        {
          key: '/content',
          icon: <BookOutlined />,
          label: '内容管理',
          children: contentSubMenu,
        } as any,
      )
    }

    // 票务管理子菜单
    const ticketingSubMenu: MenuProps['items'] = [
      { key: '/ticketing/session-pricing', label: '场次价格' },
      { key: '/ticketing/session-data', label: '场次数据', icon: <UnorderedListOutlined /> },
      { key: '/ticketing/anonymous-config', label: '匿名配置', icon: <EyeInvisibleOutlined /> },
    ]

    if (isAdmin()) {
      baseItems.push({
        key: '/ticketing',
        icon: <AccountBookOutlined />,
        label: '票务管理',
        children: ticketingSubMenu,
      } as any)
    }

    // 回放接口需要管理员权限，账号安全对所有已登录用户开放。
    if (isAdmin()) {
      baseItems.push({
        key: '/playback',
        icon: <PlayCircleOutlined />,
        label: '回放中心',
      })
    }

    baseItems.push({
      key: '/settings',
      icon: <SettingOutlined />,
      label: '账号安全',
    })

    return baseItems
  }

  const menuItems = getMenuItems()

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key)
    if (isMobile) {
      setMobileMenuVisible(false)
    }
  }

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id)
    // Handle navigation based on notification type
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const notificationMenu = {
    items: notifications.map((notification) => ({
      key: notification.id,
      label: (
        <div className="max-w-sm">
          <div className="font-medium">{notification.title}</div>
          <div className="text-sm text-gray-500">{notification.message}</div>
          <div className="text-xs text-gray-400 mt-1">
            {new Date(notification.created_at).toLocaleString()}
          </div>
        </div>
      ),
      onClick: () => handleNotificationClick(notification),
    })),
  }

  const userMenu = {
    items: [
      {
        key: 'profile',
        label: '个人资料',
        icon: <UserOutlined />,
        onClick: () => navigate('/settings/profile'),
      },
      {
        key: 'logout',
        label: '退出登录',
        icon: <LogoutOutlined />,
        onClick: logout,
      },
    ],
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        {/* 公开路由：无需登录 */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* 未知路由跳转登录页 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  const selectedKey = location.pathname

  const siderContent = (
    <div className="h-screen flex flex-col overflow-y-auto pb-4">
      <div className="p-4">
        <Title level={4} className="!mb-0 !text-primary items-center">
          O2World
        </Title>
      </div>
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        items={menuItems}
        onClick={handleMenuClick}
        className="flex-1 !border-0"
      />
    </div>
  )

  return (
    <Layout className="max-h-screen">
      {/* Mobile Header */}
      {isMobile && (
        <Header className="!bg-white !px-4 !py-0 flex items-center justify-between">
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            className=""
          />
          <Title level={4} className="!mb-0 !text-primary">
            O2World
          </Title>
          <Dropdown menu={notificationMenu} trigger={['click']} className='!border-0'>
            <Badge count={unreadCount} size="small">
              <Button type="text" icon={<BellOutlined />} />
            </Badge>
          </Dropdown>
        </Header>
      )}

      <Layout>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sider
            width={240}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            className="!bg-white shadow-sm"
            trigger={null}
          >
            {siderContent}
          </Sider>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <div
            className={`fixed inset-0 z-50 transition-opacity ${mobileMenuVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            onClick={() => setMobileMenuVisible(false)}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" />
            <div
              className={`absolute left-0 top-0 h-full w-64 bg-white shadow-lg transition-transform transform ${mobileMenuVisible ? 'translate-x-0' : '-translate-x-full'
                }`}
              onClick={(e) => e.stopPropagation()}
            >
              {siderContent}
            </div>
          </div>
        )}

        <Layout className="bg-gray-50 h-screen">
          {/* Desktop Header */}
          {!isMobile && (
            <Header className="!bg-white !px-6 !py-0 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  className=""
                />
                <Text className="text-gray-600">
                  {menuItems.find(item => item.key === selectedKey)?.label || '管理后台'}
                </Text>
              </div>
              <div className="flex items-center space-x-4">
                <Dropdown menu={notificationMenu} trigger={['click']}>
                  <Badge count={unreadCount} size="small">
                    <Button type="text" icon={<BellOutlined />} />
                  </Badge>
                </Dropdown>
                <Dropdown menu={userMenu} trigger={['click']}>
                  <Space className="cursor-pointer">
                    <Avatar size="small" icon={<UserOutlined />} />
                    <Text className="hidden lg:inline">{user?.username}</Text>
                  </Space>
                </Dropdown>
              </div>
            </Header>
          )}
          <div className='h-full w-full overflow-y-auto'>
            <Content className="" >
              <WorldProvider>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/roles" element={<RoleManagement />} />
                  {/* 归档路由（v3）：被新后台接管，统一跳 /dashboard。
                      对应 archived_prefixes.txt 中的 C 档 prefix。*/}
                  <Route path="/agents" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/admin-social" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/notifications" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/files" element={<FileManagement />} />
                  <Route path="/ads" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/scripts" element={<ScriptManagement />} />
                  <Route path="/config-blocks" element={<ConfigBlockManagement />} />
                  <Route path="/characters" element={<CharacterManagement />} />
                  <Route path="/merchants" element={<MerchantManagement />} />
                  <Route path="/venues" element={<VenueManagement />} />
                  <Route path="/rooms" element={<RoomManagement />} />
                  <Route path="/devices/:roomId" element={<DeviceManagement />} />
                  <Route path="/devices" element={<Navigate to="/devices/1" replace />} />
                  <Route path="/device-catalog" element={<DeviceCatalogManagement />} />
                  {/* AI世界模块路由 — 全部归档（/ai/worlds、/ai/rooms、/admin/players 等）*/}
                  <Route path="/ai-worlds" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/players" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/inventory" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/skills" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/tasks" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/social" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/invites" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/treasures" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/templates" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/contents" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ai-worlds/code-words" element={<Navigate to="/dashboard" replace />} />
                  {/* 票务管理模块路由 — 大部分归档，保留价格配置/匿名配置，并新增只读场次数据页 */}
                  <Route path="/ticketing/dashboard" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/orders" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/schedules" element={<Navigate to="/ticketing/session-data" replace />} />
                  <Route path="/ticketing/session-pricing" element={<TicketingSessionPricing />} />
                  <Route path="/ticketing/session-data" element={<TicketingSessionDataViewer />} />
                  <Route path="/ticketing/points-management" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/transactions" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/refund-rules" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/booking-rules" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/recharge-bonus" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/ticketing/anonymous-config" element={<TicketingAnonymousConfig />} />
                  {/* 商品管理模块路由 — 全部归档（/goods）*/}
                  <Route path="/goods-categories" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/goods" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/goods-orders" element={<Navigate to="/dashboard" replace />} />

                  {/* 系统管理模块路由 — 仅保留 A 档 */}
                  <Route path="/system/configs" element={<SystemConfigManagement />} />
                  <Route path="/system/vip-levels" element={<VIPLevelConfig />} />
                  <Route path="/system/paywall-config" element={<Navigate to="/dashboard" replace />} />

                  {/* 榜单管理模块路由 — 全部归档（/leaderboard、/admin/leaderboard）*/}
                  <Route path="/leaderboard" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/leaderboard/blessings" element={<Navigate to="/dashboard" replace />} />

                  {/* 货币管理模块路由 — 归档（/currency、/admin/currency）*/}
                  <Route path="/currency" element={<Navigate to="/dashboard" replace />} />

                  {/* 演员管理模块路由 — 归档（/performer、/admin/performers、/admin/scheduling）*/}
                  <Route path="/performers" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/scheduling" element={<Navigate to="/dashboard" replace />} />

                  {/* 标签管理模块路由 */}
                  <Route path="/tags/definitions" element={<TagDefinitionManagement />} />

                  {/* NFC 管理模块路由 */}
                  <Route path="/nfc" element={<NfcManagement />} />

                  {/* API Key 管理模块路由 */}
                  <Route path="/api-keys" element={<ApiKeysManagement />} />

                  {/* 学习资料管理模块路由 — 归档（/learning-materials）*/}
                  <Route path="/learning-materials" element={<Navigate to="/dashboard" replace />} />

                  {/* AI 剧本生成模块路由（已恢复） */}
                  <Route path="/script-generation" element={<ScriptGenerationTaskList />} />
                  <Route path="/script-generation/create" element={<ScriptGenerationCreateTask />} />
                  <Route path="/script-generation/create-v5" element={<ScriptGenerationV5CreateTask />} />
                  <Route path="/script-generation/:taskId/preview" element={<ScriptGenerationConfigPreview />} />
                  <Route path="/script-generation/:taskId" element={<ScriptGenerationTaskDetailRouter />} />

                  {/* AI 监控由场馆本地 AI Engine 提供，云端管理后台不再暴露无效入口。 */}
                  <Route path="/ai-monitoring" element={<Navigate to="/dashboard" replace />} />

                  {/* 玩家回放中心 (Phase 2: ai-engine outbox → MQTT → portal.game_records_sync) */}
                  <Route path="/playback" element={<PlaybackList />} />
                  <Route
                    path="/playback/:worldId/:playerId"
                    element={<PlaybackDetail />}
                  />

                  <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/profile" element={<Settings />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </WorldProvider>
            </Content>
          </div>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default App
